import { useEffect, useCallback, useMemo } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getProductItemModules,
  getProductCustomPropertiesData,
  getMemberProductCustomProperties,
} from 'services/product';

import { useAsync } from 'hooks/useAsync';

const getProductItemModulesKeys = (productId) => {
  return ['product-detail', 'item-modules', parseInt(productId)];
};

export const useQueryProductItemModules = (productId) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: getProductItemModulesKeys(productId),
    queryFn: async () => {
      const response = await getProductItemModules({
        productId,
      });
      return response?.data;
    },
    staleTime: Infinity,
    enabled: !!productId,
  });

  const refetchProductItems = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: getProductItemModulesKeys(productId),
    });
  }, [productId, queryClient]);

  return {
    productItemModules: useMemo(() => query?.data, [query?.data]),
    statusFetchProductItem: query?.status,
    refetchProductItems,
  };
};

const useGetProductCustomProperties = (id) => {
  const { data, run } = useAsync();

  useEffect(() => {
    if (id) {
      run(getProductCustomPropertiesData({ productId: id }));
    }
    return;
  }, [id, run]);

  const refetchProductCustomPropertiesData = useCallback(() => {
    return run(getProductCustomPropertiesData({ productId: id }));
  }, [id, run]);

  return { data, refetchProductCustomPropertiesData };
};

const useGetMemberProductCustomProperties = (ownerId) => {
  const { data, error, run } = useAsync();

  useEffect(() => {
    if (ownerId) {
      run(getMemberProductCustomProperties({ memberId: ownerId }));
    }
    return;
  }, [ownerId, run]);

  return { data, error };
};

const useScrollToFoundModule = ({ foundModule, elementByClassName }) => {
  useEffect(() => {
    if (foundModule && elementByClassName?.[0]) {
      elementByClassName[0].scrollIntoView();
    }

    return;
  }, [elementByClassName, foundModule]);
};

export {
  useGetProductCustomProperties,
  useScrollToFoundModule,
  useGetMemberProductCustomProperties,
};
